import type { CSSProperties } from "react";
import * as z from "zod/mini";
import {
  APPLE_PAY_BUTTON_STYLES,
  APPLE_PAY_BUTTON_TYPES,
  type ApplePayButtonStyle,
  type ApplePayButtonType,
} from "@connect/component-library/components/apple-pay-button";
import { dimensionSchema, normalizeDimension, parseSettingsJson } from "./button-settings-common";

// Generic per-button appearance settings for the non-Apple-Pay buttons. `style`
// follows Apple Pay's model (the button's own colour) so the one key means the same
// thing everywhere. Apple Pay keeps its native style/type/locale in
// apple-pay-button-settings.ts.

export interface ButtonDimensions {
  width?: string;
  height?: string;
  borderRadius?: string;
  paddingX?: string;
  paddingY?: string;
}

export interface CardButtonSettings extends ButtonDimensions {
  style?: ApplePayButtonStyle;
  // The card button borrows Apple Pay's `type` options for its call-to-action verb.
  type?: ApplePayButtonType;
}

export interface GooglePayButtonSettings extends ButtonDimensions {
  style?: ApplePayButtonStyle;
}

const DIMENSION_KEYS = ["width", "height", "borderRadius", "paddingX", "paddingY"] as const;

const dimensionShape = {
  width: dimensionSchema,
  height: dimensionSchema,
  borderRadius: dimensionSchema,
  paddingX: dimensionSchema,
  paddingY: dimensionSchema,
};
// Passing the const arrays straight to z.enum keeps the literal types, so the
// parsed data is already typed as ApplePayButtonStyle / ApplePayButtonType.
const style = z.catch(z.optional(z.enum(APPLE_PAY_BUTTON_STYLES)), undefined);

// Malformed top-level shapes (arrays, primitives, null) fall back to `{}`; a bad
// individual field falls back via its own `z.catch` without discarding its siblings.
const cardSchema = z.catch(
  z.object({
    ...dimensionShape,
    style,
    type: z.catch(z.optional(z.enum(APPLE_PAY_BUTTON_TYPES)), undefined),
  }),
  {},
);
const googlePaySchema = z.catch(z.object({ ...dimensionShape, style }), {});

function toDimensions(data: Record<string, string | undefined>): ButtonDimensions {
  const dims: ButtonDimensions = {};
  for (const key of DIMENSION_KEYS) {
    const normalized = normalizeDimension(data[key]);
    if (normalized) {
      dims[key] = normalized;
    }
  }
  return dims;
}

/** Inline style for a regular DOM button from its configured dimensions. */
export function buildButtonDimensionStyle(dims: ButtonDimensions | undefined): CSSProperties {
  const style: CSSProperties = {};
  if (!dims) {
    return style;
  }
  if (dims.width) {
    style.width = dims.width;
  }
  if (dims.height) {
    style.height = dims.height;
  }
  if (dims.borderRadius) {
    style.borderRadius = dims.borderRadius;
  }
  // An unset axis falls back to 0 so the provided axis still takes effect.
  if (dims.paddingX || dims.paddingY) {
    style.padding = `${dims.paddingY ?? "0px"} ${dims.paddingX ?? "0px"}`;
  }
  return style;
}

/** Parses the `cardButtonSettings` query param (JSON, URL-encoded). */
export function parseCardButtonSettings(
  raw: string | null | undefined,
): CardButtonSettings | undefined {
  const parsed = parseSettingsJson(raw);
  if (parsed === undefined) {
    return undefined;
  }
  const result = z.safeParse(cardSchema, parsed);
  if (!result.success) {
    return undefined;
  }
  const settings: CardButtonSettings = toDimensions(result.data);
  if (result.data.style) {
    settings.style = result.data.style;
  }
  if (result.data.type) {
    settings.type = result.data.type;
  }
  return Object.keys(settings).length > 0 ? settings : undefined;
}

/** Parses the `googlePayButtonSettings` query param (JSON, URL-encoded). */
export function parseGooglePayButtonSettings(
  raw: string | null | undefined,
): GooglePayButtonSettings | undefined {
  const parsed = parseSettingsJson(raw);
  if (parsed === undefined) {
    return undefined;
  }
  const result = z.safeParse(googlePaySchema, parsed);
  if (!result.success) {
    return undefined;
  }
  const settings: GooglePayButtonSettings = toDimensions(result.data);
  if (result.data.style) {
    settings.style = result.data.style;
  }
  return Object.keys(settings).length > 0 ? settings : undefined;
}
